import React, { useEffect, useState } from 'react';
import { purchaseAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Settings, Loader2, Plus, X, Mail, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function PurchaseSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    purchaseAPI.getPurchaseSettings()
      .then(res => setEmails(res.data?.po_notification_emails || []))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const addEmail = () => {
    const e = newEmail.trim();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error('Enter a valid email'); return; }
    if (emails.some(x => x.toLowerCase() === e.toLowerCase())) { toast.error('Already added'); return; }
    setEmails([...emails, e]);
    setNewEmail('');
  };

  const removeEmail = (e) => setEmails(emails.filter(x => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      const res = await purchaseAPI.updatePurchaseSettings({ po_notification_emails: emails });
      setEmails(res.data?.po_notification_emails || []);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-2xl space-y-6" data-testid="purchase-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Settings className="w-6 h-6 text-cyan-600" /> Purchase Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure how the purchase module behaves.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-cyan-600" /> PO / Purchase email recipients</CardTitle>
          <CardDescription>
            These internal addresses are automatically copied (CC) on every RFQ, purchase-request approval,
            and Purchase Order email generated by the module — in addition to the supplier and the sender.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="py-6 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2" data-testid="po-emails-list">
                {emails.length === 0 && <span className="text-xs text-muted-foreground">No recipients configured.</span>}
                {emails.map(e => (
                  <Badge key={e} variant="outline" className="bg-cyan-50 text-cyan-800 border-cyan-200 text-xs pl-2.5 pr-1 py-1 flex items-center gap-1" data-testid={`po-email-${e}`}>
                    {e}
                    <button onClick={() => removeEmail(e)} className="ml-1 hover:text-rose-600" data-testid={`po-email-remove-${e}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Add email</Label>
                  <Input value={newEmail} onChange={e => setNewEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                    placeholder="purchase@bhpl.co.in" className="h-9 text-sm" data-testid="po-email-input" />
                </div>
                <Button variant="outline" size="sm" onClick={addEmail} className="h-9" data-testid="po-email-add">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              <div className="pt-2">
                <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="po-settings-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save settings
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
